import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';

// ── Tipos ────────────────────────────────────────────────────────────────

export type CategoriaObservacao = 'COMPORTAMENTO' | 'APRENDIZAGEM' | 'SAUDE' | 'SOCIAL' | 'FAMILIAR' | 'ELOGIO' | 'OUTRO';
export type NivelAtencao = 'INFORMATIVO' | 'ATENCAO' | 'URGENTE';
export type ClassificacaoRisco = 'BEM' | 'ATENCAO' | 'PREOCUPANTE' | 'CRITICO';

export interface Observacao {
  id: string;
  alunoId: string;
  autorId: string;
  escolaId: string;
  categoria: CategoriaObservacao;
  nivelAtencao: NivelAtencao;
  titulo: string;
  descricao: string;
  dataReferencia: string;
  privado: boolean;
  criadoEm: string;
  aluno?: { id: string; nome: string; matricula?: string };
  autor?: { id: string; nome: string };
}

export interface CreateObservacaoInput {
  alunoId: string;
  autorId: string;
  escolaId: string;
  categoria: CategoriaObservacao;
  nivelAtencao?: NivelAtencao;
  titulo: string;
  descricao: string;
  dataReferencia: string;
  privado?: boolean;
}

export interface PerfilAcademico {
  alunoId: string;
  anoLetivo: number;
  frequencia: { totalDias: number; presentes: number; percentual: number };
  notas: {
    mediaGeral: number;
    materias: { materiaId: string; materiaNome: string; media: number }[];
    tendencia: 'SUBINDO' | 'ESTAVEL' | 'DESCENDO';
  };
  observacoes: { total: number; urgentes: number; atencao: number };
  risco: { score: number; classificacao: ClassificacaoRisco };
}

export interface TimelineItem {
  id: string;
  data: string;
  tipo: 'PRESENCA' | 'NOTA' | 'OBSERVACAO' | 'JUSTIFICATIVA';
  descricao: string;
  icone: string;
  cor: string;
  meta?: any;
}

export interface AlunoEmAtencao {
  alunoId: string;
  nome: string;
  matricula?: string;
  turma?: string;
  score: number;
  classificacao: ClassificacaoRisco;
  frequencia: number;
  mediaGeral: number;
}

// ── Hooks ────────────────────────────────────────────────────────────────

const BASE = '/observacoes';
const QK = 'observacoes';

export function useObservacoes(filtros?: {
  alunoId?: string;
  autorId?: string;
  escolaId?: string;
  categoria?: CategoriaObservacao;
  nivelAtencao?: NivelAtencao;
}) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [QK, filtros],
    queryFn: async () => {
      const { data } = await api.get<Observacao[]>(BASE, { params: filtros });
      return Array.isArray(data) ? data : [];
    },
    enabled: !!(filtros?.autorId || filtros?.alunoId),
  });

  const criar = useMutation({
    mutationFn: (input: CreateObservacaoInput) => api.post(BASE, input).then(r => r.data as Observacao),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QK] }),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, ...input }: Partial<Observacao> & { id: string }) =>
      api.patch(`${BASE}/${id}`, input).then(r => r.data as Observacao),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QK] }),
  });

  const remover = useMutation({
    mutationFn: (id: string) => api.delete(`${BASE}/${id}`).then(r => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QK] }),
  });

  return {
    observacoes: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    criar,
    atualizar,
    remover,
  };
}

/** Perfil acadêmico consolidado de um aluno */
export function usePerfilAcademico(alunoId: string | undefined, anoLetivo?: number) {
  return useQuery({
    queryKey: ['perfilAcademico', alunoId, anoLetivo],
    queryFn: async () => {
      if (!alunoId) return null;
      const { data } = await api.get<PerfilAcademico>(`${BASE}/perfil/${alunoId}`, { params: { anoLetivo } });
      return data;
    },
    enabled: !!alunoId,
  });
}

/** Timeline unificada de um aluno */
export function useTimeline(alunoId: string | undefined, limit = 30) {
  return useQuery({
    queryKey: ['timeline', alunoId, limit],
    queryFn: async () => {
      if (!alunoId) return [];
      const { data } = await api.get<TimelineItem[]>(`${BASE}/timeline/${alunoId}`, { params: { limit } });
      return Array.isArray(data) ? data : [];
    },
    enabled: !!alunoId,
  });
}

/** Alunos com risco elevado para o dashboard do professor */
export function useAlunosEmAtencao(professorId: string | undefined, limit = 5) {
  return useQuery({
    queryKey: ['alunosEmAtencao', professorId, limit],
    queryFn: async () => {
      if (!professorId) return [];
      const { data } = await api.get<AlunoEmAtencao[]>(`${BASE}/atencao/${professorId}`, { params: { limit } });
      return Array.isArray(data) ? data : [];
    },
    enabled: !!professorId,
    staleTime: 120_000,
  });
}
